import { createAppAsyncThunk } from "integrations/redux/hooks"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { overrideProjectLocation } from "features/entity-caching/fires/thunks"
import { getViewState, setNewViewstate } from "features/map/GlobalViewState"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setActiveContextMenu } from "features/popups/slice"
import {
    endLocationOverride,
    PROJECT_POPUP_DIMENSIONS,
    selectLocationOverride,
    setActivePopup,
    setLocationOverrideSubmitting,
    startLocationOverride,
} from "features/overview/slice"

const PLACEMENT_ZOOM = 12

export const beginProjectLocationOverride = createAppAsyncThunk(
    "overview/beginLocationOverride",
    (payload: { projectId: string }, thunkApi) => {
        const state = thunkApi.getState()
        thunkApi.dispatch(startLocationOverride(payload))
        thunkApi.dispatch(setActiveContextMenu(undefined))

        const project = selectFireById(payload.projectId)(state)
        if (project?.location) {
            setNewViewstate(
                {
                    latitude: project.location.latitude,
                    longitude: project.location.longitude,
                    zoom: Math.max(getViewState().zoom, PLACEMENT_ZOOM),
                },
                { forceFly: true, minFlyDurationMs: 800, maxFlyDurationMs: 1200 }
            )
        }
    }
)

export const cancelProjectLocationOverride = createAppAsyncThunk(
    "overview/cancelLocationOverride",
    (_: void, thunkApi) => {
        thunkApi.dispatch(endLocationOverride())
    }
)

export const submitProjectLocationOverride = createAppAsyncThunk(
    "overview/submitLocationOverride",
    async (_: void, thunkApi) => {
        const state = thunkApi.getState()
        const override = selectLocationOverride(state)
        if (!override || override.isSubmitting) return

        const { latitude, longitude } = getViewState()

        thunkApi.dispatch(setLocationOverrideSubmitting(true))
        try {
            await thunkApi
                .dispatch(
                    overrideProjectLocation({
                        projectId: override.projectId,
                        location: { latitude, longitude },
                    })
                )
                .unwrap()

            thunkApi.dispatch(
                setGlobalNotificationContext({
                    message: "New location set.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
            thunkApi.dispatch(endLocationOverride())
            thunkApi.dispatch(
                setActivePopup({
                    type: "project",
                    id: override.projectId,
                    placementType: "point",
                    point: { lat: latitude, lng: longitude },
                    renderedDimensions: PROJECT_POPUP_DIMENSIONS,
                    margin: {
                        x: -PROJECT_POPUP_DIMENSIONS.width,
                        y: -PROJECT_POPUP_DIMENSIONS.height,
                    },
                })
            )
        } catch (err) {
            console.error("Failed to update project location:", err)
            thunkApi.dispatch(
                setGlobalNotificationContext({
                    message: "Failed to update location. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
            thunkApi.dispatch(setLocationOverrideSubmitting(false))
        }
    }
)
